import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import ReactQueryProvider from "@/providers/ReactQueryProvider";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { ConfirmDialogProvider } from "@/providers/ConfirmDialogProvider";
import AuthProviderV2 from "@/providers/AuthProvider";
import NonAuthenticatedLayout from "@/layouts/NonAuthenticatedLayout";
import AuthenticatedLayout from "@/layouts/AuthenticatedLayout";

// Basic pages
import Index from "@/pages/Index";
import NotFoundPage from "@/pages/404";

// Auth pages
import Login from "@/pages/auth/Login";
import TestLogin from "@/pages/auth/TestLogin";
import SocialCallback from "@/pages/auth/SocialCallback";
import Transfer from "@/pages/auth/Transfer";
import ConfirmEmail from "@/pages/auth/ConfirmEmail";
import MagicLink from "@/pages/auth/MagicLink";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Invites from "@/pages/auth/Invites";
import ResendConfirmation from "@/pages/auth/ResendConfirmation";
import LoggedOut from "@/pages/auth/LoggedOut";
import SessionExpired from "@/pages/auth/SessionExpired";
import StripeCallback from "@/pages/auth/StripeCallback";

// Anythink pages
import ApiDocs from "@/pages/breeze/ApiDocs";
import Dashboard from "@/pages/breeze/Dashboard";

// Anythink entities pages
import AllEntitiesPage from "@/pages/breeze/entities/All";
import EntityOverviewPage from "@/pages/breeze/entities/Overview";
import CreateEntityPage from "@/pages/breeze/entities/Create";
import EntityViewSinglePage from "@/pages/breeze/entities/ViewSingle";

import HelpPage from "@/pages/breeze/Help";
import FilesPage from "@/pages/breeze/FileBrowser";
import MyAccountPage from "@/pages/breeze/MyAccount";
import SettingsPage from "@/pages/breeze/settings/Settings";

// Anythink settings pages
import OrganisationSettingsPage from "@/pages/breeze/settings/Organisation";
import DataModelPage from "@/pages/breeze/settings/DataModel";
import MenuPage from "@/pages/breeze/settings/Menu";
import EmailTemplatesPage from "@/pages/breeze/settings/EmailTemplates";
import PushNotificationsPage from "@/pages/breeze/settings/PushNotifications";
import IntegrationsSettingsPage from "@/pages/breeze/settings/Integrations";
import IntegrationCallbackPage from "@/pages/breeze/settings/IntegrationCallback";
import RolesAndPermissionsPage from "@/pages/breeze/settings/roles-and-permissions/All";
import EditRolePage from "@/pages/breeze/settings/roles-and-permissions/EditRole";
import GroupsPage from "@/pages/breeze/settings/groups/All";
import EditGroupPage from "@/pages/breeze/settings/groups/EditGroup";
import UsersPage from "@/pages/breeze/settings/users/All";
import EditUserPage from "@/pages/breeze/settings/users/EditUser";
import SecretsPage from "@/pages/breeze/settings/secrets/All";
import StripeConnectPage from "@/pages/breeze/settings/StripeConnect";
import OperationsPage from "@/pages/breeze/settings/Operations";
import AISettingsPage from "@/pages/breeze/settings/AISettings";

// Anythink workflows pages
import WorkflowsPage from "@/pages/breeze/workflows/All";
import CreateWorkflowPage from "@/pages/breeze/workflows/Create";
import WorkflowPage from "@/pages/breeze/workflows/EditWorkflow";

// AnythinkPay pages
import AnythinkPayOverviewPage from "@/pages/breeze/integrations/anythinkpay/Overview";
import PaymentSuccessPage from "@/pages/breeze/integrations/anythinkpay/PaymentSuccess";
import PaymentCancelledPage from "@/pages/breeze/integrations/anythinkpay/PaymentCancelled";
import SubscriptionsPage from "@/pages/breeze/integrations/anythinkpay/Subscriptions";
import CreateSubscriptionPage from "./pages/breeze/integrations/anythinkpay/CreateSubscription";
import ViewSubscriptionPage from "./pages/breeze/integrations/anythinkpay/ViewSubscription";
import SubscriptionSuccessPage from "@/pages/breeze/integrations/anythinkpay/SubscriptionSuccess";
import SubscriptionCancelledPage from "@/pages/breeze/integrations/anythinkpay/SubscriptionCancelled";
import CreateSubscriptionPlanPage from "./pages/breeze/integrations/anythinkpay/CreateSubscriptionPlan";
import EditSubscriptionPlanPage from "./pages/breeze/integrations/anythinkpay/EditSubscriptionPlan";

// Growth pages
import GrowthPage from "@/pages/breeze/growth/Growth";

// AI Agents pages
import AllAIAgentsPage from "@/pages/breeze/ai-agents/All";
import CreateAIAgentPage from "@/pages/breeze/ai-agents/Create";
import AIAgentChatPage from "@/pages/breeze/ai-agents/Chat";

// HOCs
import withFeatureFlagRequired from "@/hoc/withFeatureFlagRequired";

// Wrap AI Agent pages with feature flag protection
const ProtectedAllAIAgentsPage = withFeatureFlagRequired(
  "AnythinkAIAgent",
  AllAIAgentsPage,
);
const ProtectedCreateAIAgentPage = withFeatureFlagRequired(
  "AnythinkAIAgent",
  CreateAIAgentPage,
);
const ProtectedAIAgentChatPage = withFeatureFlagRequired(
  "AnythinkAIAgent",
  AIAgentChatPage,
);

try {
  // Remove old auth sessions
  localStorage.removeItem("anythink_auth_session");
  localStorage.removeItem("anythink_ui_auth_session");
  localStorage.removeItem("myanythink_auth_session");
} catch {}

function PlanEditRedirect() {
  const { planId } = useParams();
  return (
    <Navigate to={`../../subscriptions/plans/${planId}`} replace />
  );
}

function TemplateEditRedirect() {
  const { planId } = useParams();
  return (
    <Navigate to={`../subscriptions/plans/${planId}`} replace />
  );
}

function App() {
  return (
    <>
      <ReactQueryProvider>
        <ThemeProvider>
          <ConfirmDialogProvider>
            <AuthProviderV2>
              <Routes>
                {/* Non-authenticated routes */}
                <Route
                  path="/org/:orgId/auth"
                  element={<NonAuthenticatedLayout />}
                >
                  <Route path="login" element={<Login />} />
                  <Route path="test-login" element={<TestLogin />} />
                  <Route path=":provider/callback" element={<SocialCallback />} />
                  <Route path="transfer" element={<Transfer />} />
                  <Route path="confirm-email" element={<ConfirmEmail />} />
                  <Route
                    path="resend-confirmation"
                    element={<ResendConfirmation />}
                  />
                  <Route path="magic-link" element={<MagicLink />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  <Route path="invites/:inviteId" element={<Invites />} />
                  <Route path="logged-out" element={<LoggedOut />} />
                  <Route path="session-expired" element={<SessionExpired />} />
                  <Route path="stripe-callback" element={<StripeCallback />} />
                </Route>

                {/* Public payment result pages (no auth required) */}
                <Route
                  path="/org/:orgId/integrations/anythinkpay/payments/success"
                  element={<PaymentSuccessPage />}
                />
                <Route
                  path="/org/:orgId/integrations/anythinkpay/payments/cancelled"
                  element={<PaymentCancelledPage />}
                />

                {/* Public subscription result pages (no auth required) */}
                <Route
                  path="/org/:orgId/integrations/anythinkpay/subscriptions/success"
                  element={<SubscriptionSuccessPage />}
                />
                <Route
                  path="/org/:orgId/integrations/anythinkpay/subscriptions/cancelled"
                  element={<SubscriptionCancelledPage />}
                />

                {/* Authenticated routes */}
                <Route path="/org/:orgId" element={<AuthenticatedLayout />}>
                  <Route index element={<Index />} />
                  <Route path="api-docs" element={<ApiDocs />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="dashboard/:dashboardId" element={<Dashboard />} />
                  <Route path="entities">
                    <Route index element={<AllEntitiesPage />} />
                    <Route
                      path=":entityName"
                      element={<EntityOverviewPage />}
                    />
                    <Route
                      path=":entityName/create"
                      element={<CreateEntityPage />}
                    />
                    <Route
                      path=":entityName/:id"
                      element={<EntityViewSinglePage />}
                    />
                  </Route>
                  <Route path="files" element={<FilesPage />} />
                  <Route path="help" element={<HelpPage />} />
                  <Route path="my-account" element={<MyAccountPage />} />
                  <Route path="settings">
                    <Route index element={<SettingsPage />} />
                    <Route
                      path="organisation"
                      element={<OrganisationSettingsPage />}
                    />
                    <Route path="data-model" element={<DataModelPage />} />
                    <Route path="menu" element={<MenuPage />} />
                    <Route
                      path="email-templates"
                      element={<EmailTemplatesPage />}
                    />
                    <Route
                      path="push-notifications"
                      element={<PushNotificationsPage />}
                    />
                    <Route path="roles-and-permissions">
                      <Route index element={<RolesAndPermissionsPage />} />
                      <Route path="role/:roleId" element={<EditRolePage />} />
                    </Route>
                    <Route path="groups">
                      <Route index element={<GroupsPage />} />
                      <Route path=":groupId" element={<EditGroupPage />} />
                    </Route>
                    <Route path="users">
                      <Route index element={<UsersPage />} />
                      <Route path=":userId" element={<EditUserPage />} />
                    </Route>
                    <Route path="secrets" element={<SecretsPage />} />
                    <Route path="payments" element={<StripeConnectPage />} />
                    {/* Back-compat: old Stripe Connect URL + in-flight Stripe OAuth callbacks. */}
                    <Route
                      path="stripe-connect"
                      element={<Navigate to="../payments" replace />}
                    />
                    <Route path="operations" element={<OperationsPage />} />
                    <Route path="ai" element={<AISettingsPage />} />
                    <Route
                      path="integrations"
                      element={<IntegrationsSettingsPage />}
                    />
                    <Route
                      path="integrations/callback"
                      element={<IntegrationCallbackPage />}
                    />
                  </Route>
                  <Route path="workflows">
                    <Route index element={<WorkflowsPage />} />
                    <Route path="create" element={<CreateWorkflowPage />} />
                    <Route path=":workflowId" element={<WorkflowPage />} />
                  </Route>

                  <Route path="growth" element={<GrowthPage />} />

                  <Route path="ai-agents">
                    <Route index element={<ProtectedAllAIAgentsPage />} />
                    <Route
                      path="create"
                      element={<ProtectedCreateAIAgentPage />}
                    />
                    <Route
                      path=":agentId/chat"
                      element={<ProtectedAIAgentChatPage />}
                    />
                  </Route>

                  <Route path="integrations">
                    <Route path="anythinkpay">
                      <Route index element={<AnythinkPayOverviewPage />} />
                      {/* Payments now live as a tab inside the unified hub. Keep the route as a
                          deep-link redirect so any external/bookmarked URLs still work. */}
                      <Route
                        path="payments"
                        element={
                          <Navigate to="../subscriptions?tab=payments" replace />
                        }
                      />
                      <Route path="subscriptions">
                        <Route index element={<SubscriptionsPage />} />
                        <Route
                          path="create"
                          element={<CreateSubscriptionPage />}
                        />
                        <Route path="plans">
                          <Route
                            index
                            element={
                              <Navigate to="../?tab=plans" replace />
                            }
                          />
                          <Route
                            path="create"
                            element={<CreateSubscriptionPlanPage />}
                          />
                          <Route
                            path=":planId"
                            element={<EditSubscriptionPlanPage />}
                          />
                        </Route>
                        <Route path=":id" element={<ViewSubscriptionPage />} />
                      </Route>
                      <Route path="subscription-plans">
                        <Route
                          index
                          element={
                            <Navigate to="../subscriptions?tab=plans" replace />
                          }
                        />
                        <Route
                          path="create"
                          element={
                            <Navigate
                              to="../../subscriptions/plans/create"
                              replace
                            />
                          }
                        />
                        <Route
                          path=":planId"
                          element={<PlanEditRedirect />}
                        />
                      </Route>
                      {/* Backward-compat redirects for the old templates URLs. */}
                      <Route
                        path="subscription-templates"
                        element={
                          <Navigate to="../subscriptions?tab=plans" replace />
                        }
                      />
                      <Route
                        path="subscription-templates/create"
                        element={
                          <Navigate
                            to="../subscriptions/plans/create"
                            replace
                          />
                        }
                      />
                      <Route
                        path="subscription-templates/:planId"
                        element={<TemplateEditRedirect />}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* 404 page */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AuthProviderV2>
          </ConfirmDialogProvider>
        </ThemeProvider>
      </ReactQueryProvider>
      <Toaster containerStyle={{ zIndex: 9999999 }} />
    </>
  );
}

export default App;
